import React, { useState, useEffect } from "react";
import {
  FiActivity,
  FiTrendingUp,
  FiTarget,
  FiCalendar,
} from "react-icons/fi";
import MonthlySalesChart from "../../../../tailadmin/components/ecommerce/MonthlySalesChart";
import PieChart from "../../../../tailadmin/components/ecommerce/PieChart";
import reports from "../../../../services/reports";
import Skeleton from "../../../ui/Skeleton";
import ExerciseLogsModal, { ExerciseLogs } from "./ExerciseLogsModal";

interface StudentWorkoutTrackingDashboardProps {
  studentId: string | number;
  professionalId?: string | number;
}

export default function StudentWorkoutTrackingDashboard({
  studentId,
  professionalId,
}: StudentWorkoutTrackingDashboardProps) {
  const [loading, setLoading] = useState(true);
  const [dateFilter, setDateFilter] = useState<number | null>(30);
  const [selectedExercise, setSelectedExercise] = useState<{
    exercise_id: string;
    exercise_name: string;
  } | null>(null);
  const [exerciseLogs, setExerciseLogs] = useState<ExerciseLogs | null>(null);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [stats, setStats] = useState({
    total_workouts: 0,
    frequency_by_day: [] as { date: string; count: number }[],
    workout_plan_stats: [] as {
      plan_id: number;
      plan_name: string;
      count: number;
    }[],
    top_exercises: [] as {
      exercise_id: string;
      exercise_name: string;
      count: number;
    }[],
    weekday_stats: [] as { day: string; count: number }[],
    adherence_rate: 0,
    days_analyzed: 30,
    days_with_workout: 0,
    word_cloud_image_url: "" as string | undefined,
  });

  useEffect(() => {
    async function fetchData() {
      if (!studentId) return;

      setLoading(true);
      try {
        const data = await reports.getStudentWorkoutStats(
          studentId,
          professionalId,
          dateFilter || undefined,
        );
        setStats({
          ...data,
          word_cloud_image_url: data.word_cloud_image_url || "",
        });
      } catch (error) {
        console.error("Erro ao buscar estatísticas de treino:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [studentId, professionalId, dateFilter]);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return "";
    const date = new Date(dateStr);
    return date.toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
    });
  };

  const frequencyCategories = stats.frequency_by_day.map((d) =>
    formatDate(d.date),
  );
  const frequencyData = stats.frequency_by_day.map((d) => d.count);

  const workoutPlanLabels = stats.workout_plan_stats.map((p) => p.plan_name);
  const workoutPlanData = stats.workout_plan_stats.map((p) => p.count);

  const weekdayLabels = stats.weekday_stats.map((w) => w.day);
  const weekdayData = stats.weekday_stats.map((w) => w.count);

  const filterOptions = [
    { label: "7 dias", value: 7 },
    { label: "15 dias", value: 15 },
    { label: "30 dias", value: 30 },
    { label: "60 dias", value: 60 },
    { label: "90 dias", value: 90 },
  ];

  const handleExerciseClick = async (exercise: {
    exercise_id: string;
    exercise_name: string;
  }) => {
    setSelectedExercise(exercise);
    setLoadingLogs(true);
    try {
      const logs = await reports.getExerciseLogs(
        studentId,
        exercise.exercise_id,
        professionalId,
        dateFilter || undefined,
      );
      setExerciseLogs(logs);
    } catch (error) {
      console.error("Erro ao buscar logs do exercício:", error);
      setExerciseLogs(null);
    } finally {
      setLoadingLogs(false);
    }
  };

  const handleCloseModal = () => {
    setSelectedExercise(null);
    setExerciseLogs(null);
  };

  if (loading) {
    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="rounded-xl sm:rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]"
            >
              <Skeleton className="h-8 w-8 sm:h-10 sm:w-10 rounded-full" />
              <div className="mt-3 sm:mt-4">
                <Skeleton className="mb-2 h-5 sm:h-6 w-20 sm:w-24" />
                <Skeleton className="h-3 sm:h-4 w-28 sm:w-32" />
              </div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {[...Array(2)].map((_, i) => (
            <div
              key={i}
              className="rounded-lg sm:rounded-sm border border-stroke bg-white px-4 sm:px-5 pb-4 sm:pb-5 pt-5 sm:pt-7.5 shadow-default dark:border-strokedark dark:bg-boxdark"
            >
              <Skeleton className="mb-3 h-5 sm:h-6 w-32 sm:w-40" />
              <Skeleton className="h-[250px] sm:h-[350px] w-full" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header com filtro */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h4 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white">
            Acompanhamento de Treinos
          </h4>
          <p className="mt-1 text-xs sm:text-sm text-gray-600 dark:text-gray-400">
            Acompanhe o desempenho e frequência de treinos do aluno
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {filterOptions.map((option) => (
            <button
              key={option.label}
              onClick={() => setDateFilter(option.value)}
              className={`rounded-lg sm:rounded-md px-3 py-2 sm:py-1.5 text-xs sm:text-sm font-medium transition active:scale-95 touch-manipulation ${
                dateFilter === option.value
                  ? "bg-purple-600 text-white shadow-sm"
                  : "bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-meta-4 dark:text-gray-300 dark:hover:bg-opacity-80"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* Métricas */}
      <div className="grid grid-cols-1 gap-3 sm:gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Total de Treinos */}
        <div className="rounded-xl sm:rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-lg sm:rounded-xl bg-purple-100 dark:bg-purple-900/30">
            <FiActivity className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600 dark:text-purple-400" />
          </div>
          <div className="mt-4 sm:mt-5">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Total de Treinos
            </p>
            <h4 className="mt-1.5 sm:mt-2 text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {stats.total_workouts}
            </h4>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Nos últimos {stats.days_analyzed} dias
            </p>
          </div>
        </div>

        {/* Taxa de Aderência */}
        <div className="rounded-xl sm:rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-lg sm:rounded-xl bg-green-100 dark:bg-green-900/30">
            <FiTarget className="h-5 w-5 sm:h-6 sm:w-6 text-green-600 dark:text-green-400" />
          </div>
          <div className="mt-4 sm:mt-5">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Taxa de Aderência
            </p>
            <h4 className="mt-1.5 sm:mt-2 text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {stats.adherence_rate}%
            </h4>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              {stats.days_with_workout} dias com treino
            </p>
          </div>
        </div>

        {/* Dias com Treino */}
        <div className="rounded-xl sm:rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-lg sm:rounded-xl bg-blue-100 dark:bg-blue-900/30">
            <FiCalendar className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600 dark:text-blue-400" />
          </div>
          <div className="mt-4 sm:mt-5">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Dias Ativos
            </p>
            <h4 className="mt-1.5 sm:mt-2 text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {stats.days_with_workout}
            </h4>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              De {stats.days_analyzed} dias analisados
            </p>
          </div>
        </div>

        {/* Média Diária */}
        <div className="rounded-xl sm:rounded-2xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm dark:border-gray-800 dark:bg-white/[0.03]">
          <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-lg sm:rounded-xl bg-orange-100 dark:bg-orange-900/30">
            <FiTrendingUp className="h-5 w-5 sm:h-6 sm:w-6 text-orange-600 dark:text-orange-400" />
          </div>
          <div className="mt-4 sm:mt-5">
            <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
              Média Diária
            </p>
            <h4 className="mt-1.5 sm:mt-2 text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
              {stats.days_analyzed > 0
                ? (stats.total_workouts / stats.days_analyzed).toFixed(1)
                : "0"}
            </h4>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Treinos por dia
            </p>
          </div>
        </div>
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Frequência de Treinos e Nuvem de Palavras - lado a lado */}
        {(stats.frequency_by_day.length > 0 || stats.word_cloud_image_url) && (
          <>
            {stats.frequency_by_day.length > 0 && (
              <div className="col-span-1 h-[300px] sm:h-[350px] lg:h-[400px]">
                <MonthlySalesChart
                  title="Frequência de Treinos"
                  data={frequencyData}
                  categories={frequencyCategories}
                />
              </div>
            )}
            {stats.word_cloud_image_url && (
              <div
                className={`col-span-1 h-[300px] sm:h-[350px] lg:h-[400px] ${
                  stats.frequency_by_day.length === 0 ? "lg:col-span-2" : ""
                }`}
              >
                <div className="flex h-full flex-col rounded-xl sm:rounded-2xl border border-stroke bg-white shadow-sm dark:border-strokedark dark:bg-boxdark">
                  <div className="border-b border-stroke px-4 sm:px-6 pt-4 sm:pt-6 pb-3 sm:pb-4 dark:border-strokedark">
                    <div className="flex items-center justify-between">
                      <h3 className="text-base sm:text-lg font-semibold text-gray-800 dark:text-white/90">
                        Exercícios Mais Realizados
                      </h3>
                      <div className="group relative inline-block">
                        <button className="flex items-center justify-center rounded-lg p-1.5 sm:p-2 hover:bg-gray-100 dark:hover:bg-gray-800 active:scale-95 touch-manipulation">
                          <FiActivity
                            className="text-gray-500 dark:text-gray-400"
                            size={14}
                          />
                        </button>
                        <div className="pointer-events-none absolute bottom-full right-0 mb-2 hidden w-44 sm:w-48 rounded bg-gray-800 p-2 text-center text-xs text-white opacity-0 transition-opacity group-hover:block group-hover:opacity-100 z-50">
                          Nuvem de palavras com os nomes dos exercícios mais
                          realizados pelo aluno no período selecionado. As
                          palavras maiores indicam exercícios mais frequentes.
                          <div className="absolute right-3 top-full -ml-1 h-0 w-0 border-4 border-solid border-transparent border-t-gray-800"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-1 items-center justify-center overflow-hidden p-3 sm:p-4">
                    <img
                      src={stats.word_cloud_image_url}
                      alt="Exercícios Mais Realizados"
                      className="h-full w-full object-contain"
                    />
                  </div>
                </div>
              </div>
            )}
          </>
        )}

        {stats.weekday_stats.length > 0 && (
          <PieChart
            title="Treinos por Dia da Semana"
            data={weekdayData}
            labels={weekdayLabels}
          />
        )}

        {stats.workout_plan_stats.length > 0 && (
          <PieChart
            title="Treinos por Plano"
            data={workoutPlanData}
            labels={workoutPlanLabels}
          />
        )}
      </div>

      {/* Top Exercícios */}
      {stats.top_exercises.length > 0 && (
        <div className="rounded-xl sm:rounded-2xl border border-stroke bg-white p-4 sm:p-6 shadow-sm dark:border-strokedark dark:bg-boxdark">
          <h5 className="mb-3 sm:mb-4 text-base sm:text-lg font-bold text-gray-900 dark:text-white">
            Exercícios Mais Realizados
          </h5>
          <div className="space-y-2 sm:space-y-3 max-h-[280px] sm:max-h-[320px] overflow-y-auto pr-1 sm:pr-2 custom-scrollbar">
            {stats.top_exercises.slice(0, 10).map((exercise, index) => (
              <div
                key={exercise.exercise_id}
                onClick={() => handleExerciseClick(exercise)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    handleExerciseClick(exercise);
                  }
                }}
                role="button"
                tabIndex={0}
                className="flex cursor-pointer items-center justify-between rounded-lg border border-gray-200 bg-gray-50 p-3 sm:p-4 transition-all active:scale-[0.98] touch-manipulation hover:border-purple-300 hover:bg-purple-50 focus:outline-none focus:ring-2 focus:ring-purple-500 dark:border-gray-700 dark:bg-gray-800/50 dark:hover:border-purple-700 dark:hover:bg-purple-900/20"
              >
                <div className="flex items-center gap-2 sm:gap-3 min-w-0 flex-1">
                  <div className="flex h-7 w-7 sm:h-8 sm:w-8 flex-shrink-0 items-center justify-center rounded-full bg-purple-100 text-xs sm:text-sm font-bold text-purple-600 dark:bg-purple-900/30 dark:text-purple-400">
                    {index + 1}
                  </div>
                  <span className="font-medium text-sm sm:text-base text-gray-900 dark:text-white truncate">
                    {exercise.exercise_name}
                  </span>
                </div>
                <span className="rounded-full bg-purple-100 px-2 sm:px-3 py-1 text-xs sm:text-sm font-semibold text-purple-700 dark:bg-purple-900/40 dark:text-purple-300 whitespace-nowrap flex-shrink-0 ml-2">
                  {exercise.count} treinos
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Estado vazio */}
      {stats.total_workouts === 0 && !loading && (
        <div className="flex flex-col items-center justify-center rounded-xl sm:rounded-2xl border-2 border-dashed border-gray-300 bg-white py-12 sm:py-16 px-4 dark:border-gray-700 dark:bg-boxdark">
          <div className="rounded-full bg-gradient-to-br from-purple-100 to-purple-100 p-5 sm:p-6 dark:from-purple-900/30 dark:to-purple-900/30">
            <FiActivity
              size={40}
              className="sm:w-12 sm:h-12 text-gray-400 dark:text-gray-600"
            />
          </div>
          <p className="mt-4 sm:mt-5 text-base sm:text-lg font-semibold text-gray-700 dark:text-gray-300 text-center">
            Nenhum treino registrado
          </p>
          <p className="mt-2 text-xs sm:text-sm text-gray-500 dark:text-gray-500 text-center max-w-sm">
            Os treinos realizados aparecerão aqui quando o aluno começar a
            treinar
          </p>
        </div>
      )}

      {/* Modal de Logs do Exercício */}
      <ExerciseLogsModal
        isOpen={!!selectedExercise}
        onClose={handleCloseModal}
        exerciseName={
          exerciseLogs?.exercise_name ||
          selectedExercise?.exercise_name ||
          "Logs do Exercício"
        }
        exerciseLogs={exerciseLogs}
        loading={loadingLogs}
      />
    </div>
  );
}
